/* =============================================================================
 * consent.js — cookie consent (per category) + tracker gating for interview.clozo.ai
 *
 * WHY: PostHog analytics and the six advertising pixels are non-essential. Under
 * GDPR/ePrivacy, Quebec Law 25 and the CCPA the site must not load them until the
 * visitor opts in, must let them choose PER CATEGORY, must honour Global Privacy
 * Control / Do Not Track as an opt-out AND display that it did, and must never
 * track on the account pages. This file is what makes the Cookie Policy true.
 *
 * TWO LAYERS, as the Cookie Policy describes:
 *   layer 1 — banner: "Cookie settings" | "Reject all" | "Accept all"
 *   layer 2 — panel:  Strictly necessary (always on) / Analytics / Advertising
 * "Your Privacy Choices" and "Cookie settings" in the footer reopen layer 2 with
 * the current state, so consent can be withdrawn as easily as it was given.
 *
 * SELF-CONTAINED ON PURPOSE: no external host, no inline handlers, all styling
 * via .consent-* classes in styles.css — so it runs unchanged under pricing.html's
 * stricter <meta> CSP ('self' scripts only, no inline).
 *
 * Loaded from components/navbar.html AFTER analytics.js (window.__clozoInitAnalytics)
 * and pixels.js (window.__clozoPixels), BEFORE checkout.js. Run bake-components.sh
 * to publish. Edit here only.
 * ============================================================================= */
(function () {
  'use strict';

  var STORE = 'clozo_consent';
  /* Stored value is JSON {"analytics":bool,"advertising":bool,"v":1}. The two
     legacy string values ('granted'/'denied') written by the first release are
     still read, so nobody is re-prompted or silently re-tracked on upgrade. */

  // Pages where PostHog and the advertising pixels never run (and the banner is
  // never shown), whatever the visitor has chosen elsewhere: every page that
  // carries a credential, a capability link or account data — sign-in, reset,
  // verify, account, manage, cancel, welcome, app sign-in, book unsubscribe
  // (LEGAL LC-6, CONTRACT Δ17) — plus support and 404; no conversion happens on
  // any of them. NOTE: /success is NOT in this list — but since billing v2 WP6
  // (A6-12) it carries the strict per-page CSP (below), so no PostHog, pixel or
  // Purchase conversion runs there either; the first-party trial_start event stays
  // (success.js, R11). This list governs ONLY PostHog + pixels. Microsoft Clarity
  // and the site's first-party events run on every user-facing page for every
  // visitor, without waiting for this banner (D12.10, D13.3, D15.3) —
  // site-runtime.js, not here — except on a strict-CSP page (no Clarity there).
  var SENSITIVE = ['/login', '/reset', '/verify', '/account', '/manage', '/cancel', '/welcome', '/app-signin',
    '/support', '/book-unsubscribe', '/404',
    '/join', '/affiliates/welcome', '/affiliates/portal'];

  function path() {
    var p = (window.location.pathname || '/');
    p = p.replace(/\.html$/, '').replace(/\/index$/, '');
    return p === '' ? '/' : p;
  }
  // Billing v2 WP6 (A6-12): a page that carries <meta http-equiv="Content-Security-Policy" data-clozo-csp="strict"> —
  // account, success, cancel, the affiliate portal: the session token, money actions, capability links — runs NO
  // third-party script at all (its CSP would refuse one anyway): treated as sensitive, so no PostHog, no pixel, no banner.
  function strictPage() {
    try { return !!document.querySelector('meta[http-equiv="Content-Security-Policy"][data-clozo-csp="strict"]'); }
    catch (e) { return false; }
  }
  function isSensitive() {
    if (strictPage()) return true;
    var p = path();
    for (var i = 0; i < SENSITIVE.length; i++) {
      if (p === SENSITIVE[i] || p.indexOf(SENSITIVE[i] + '/') === 0) return true;
    }
    return false;
  }
  // Global Privacy Control / Do Not Track = a legally recognised opt-out signal.
  function gpc() {
    try {
      return navigator.globalPrivacyControl === true ||
             navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes' ||
             window.doNotTrack === '1';
    } catch (e) { return false; }
  }

  /** Current choice, or null when the visitor has not decided yet. */
  function readPrefs() {
    var raw = null;
    try { raw = window.localStorage.getItem(STORE); } catch (e) { return null; }
    if (!raw) return null;
    if (raw === 'granted') return { analytics: true,  advertising: true };
    if (raw === 'denied')  return { analytics: false, advertising: false };
    try {
      var o = JSON.parse(raw);
      if (o && typeof o === 'object') {
        return { analytics: o.analytics === true, advertising: o.advertising === true };
      }
    } catch (e) {}
    return null;
  }
  function writePrefs(p) {
    try {
      window.localStorage.setItem(STORE, JSON.stringify({
        analytics: p.analytics === true, advertising: p.advertising === true, v: 1
      }));
    } catch (e) {}
  }

  /* The single choke-point: everything non-essential starts or stops here, and
     a withdrawal bites immediately rather than at the next page load. */
  function applyPrefs(p) {
    if (isSensitive()) return;
    // Analytics — PostHog.
    if (p.analytics) {
      try { if (typeof window.__clozoInitAnalytics === 'function') window.__clozoInitAnalytics(); } catch (e) {}
    } else {
      try { if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') window.posthog.opt_out_capturing(); } catch (e) {}
    }
    // Advertising — the six pixels, and the ad CLICK IDENTIFIERS (gclid/fbclid…),
    // which are advertising data and so are captured only with this consent. The
    // landing URL is still current here, so waiting for consent costs nothing.
    if (p.advertising) {
      try { if (window.__clozoPixels && typeof window.__clozoPixels.init === 'function') window.__clozoPixels.init(); } catch (e) {}
      try { if (window.clozo && typeof window.clozo.captureClickIds === 'function') window.clozo.captureClickIds(); } catch (e) {}
    } else {
      try { if (window.__clozoPixels && typeof window.__clozoPixels.stop === 'function') window.__clozoPixels.stop(); } catch (e) {}
      // Withdrawal must also ERASE what was already collected, or the click ids
      // would sit in localStorage and still be attached to a later purchase.
      try { if (window.clozo && typeof window.clozo.forgetClickIds === 'function') window.clozo.forgetClickIds(); } catch (e) {}
    }
  }

  /* Read by checkout.js before it attaches attribution to a purchase: consent
     can be withdrawn between the click and the checkout, and the answer must be
     the CURRENT state, not whatever was stored when the visitor landed. */
  window.__clozoConsent = {
    advertising: function () {
      if (isSensitive()) return false;
      var p = readPrefs();
      if (p) return p.advertising === true;   // an explicit choice wins over GPC
      return false;                            // undecided, or GPC with no choice
    },
    analytics: function () {
      if (isSensitive()) return false;
      var p = readPrefs();
      return !!(p && p.analytics === true);
    }
  };

  var bannerEl = null;
  function el(html) { var d = document.createElement('div'); d.innerHTML = html; return d.firstChild; }

  function buildBanner() {
    var node = el(
      '<section class="consent-banner" role="dialog" aria-label="Cookie choices" aria-live="polite">' +
        '<div class="consent-inner">' +
          '<div class="consent-layer consent-first">' +
            '<p class="consent-text">We use cookies for analytics and, with your permission, advertising. ' +
              'Essential cookies that make the site work are always on. ' +
              'See our <a href="/cookie-policy.html">Cookie Policy</a> and <a href="/privacy.html">Privacy Policy</a>.</p>' +
            '<div class="consent-actions">' +
              '<button type="button" class="consent-btn consent-settings">Cookie settings</button>' +
              '<button type="button" class="consent-btn consent-reject">Reject all</button>' +
              '<button type="button" class="consent-btn consent-accept">Accept all</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</section>'
    );
    wire(node);
    return node;
  }

  /* The category panel ("Cookie settings", "Your Privacy Choices") is built the first time it opens: a first visit
     that only answers the banner never carries its 22 hidden elements (home-page DOM size). */
  function buildPanel() {
    var node = el(
          '<div class="consent-layer consent-panel" hidden>' +
            '<p class="consent-gpc" hidden>Your browser is sending an opt-out preference signal (Global Privacy Control). ' +
              '<strong>We have honoured it</strong> and treated it as an opt-out of the sale and sharing of your personal information. ' +
              'You can still turn a category on below if you want to.</p>' +
            '<ul class="consent-cats">' +
              '<li><label><input type="checkbox" checked disabled> <span><strong>Strictly necessary</strong> — sign-in, secure checkout, fraud prevention and remembering this choice. Always on.</span></label></li>' +
              '<li><label><input type="checkbox" class="consent-cat" data-cat="analytics"> <span><strong>Analytics</strong> — how the site is used, so we can improve it (PostHog).</span></label></li>' +
              '<li><label><input type="checkbox" class="consent-cat" data-cat="advertising"> <span><strong>Advertising</strong> — measuring and targeting our ads (Google, Meta, Microsoft, LinkedIn, Reddit, X).</span></label></li>' +
            '</ul>' +
            '<div class="consent-actions">' +
              '<button type="button" class="consent-btn consent-reject">Reject all</button>' +
              '<button type="button" class="consent-btn consent-save">Save choices</button>' +
            '</div>' +
          '</div>'
    );
    wire(node);
    return node;
  }

  // No inline handlers anywhere — pricing.html's CSP forbids them.
  function wire(node) {
    node.querySelectorAll('.consent-accept').forEach(function (b) {
      b.addEventListener('click', function () { choose({ analytics: true, advertising: true }); });
    });
    node.querySelectorAll('.consent-save').forEach(function (b) {
      b.addEventListener('click', saveFromPanel);
    });
    node.querySelectorAll('.consent-reject').forEach(function (b) {
      b.addEventListener('click', function () { choose({ analytics: false, advertising: false }); });
    });
    node.querySelectorAll('.consent-settings').forEach(function (b) {
      b.addEventListener('click', function () { openPanel(); });
    });
  }

  function ensureBanner() {
    if (!bannerEl) { bannerEl = buildBanner(); document.body.appendChild(bannerEl); }
    return bannerEl;
  }
  function ensurePanel() {
    var pane = bannerEl.querySelector('.consent-panel');
    if (!pane) { pane = buildPanel(); bannerEl.querySelector('.consent-inner').appendChild(pane); }
    return pane;
  }
  function setPanelState(p) {
    if (!bannerEl) return;
    bannerEl.querySelectorAll('.consent-cat').forEach(function (input) {
      input.checked = !!(p && p[input.getAttribute('data-cat')]);
    });
    var g = bannerEl.querySelector('.consent-gpc');
    if (g) g.hidden = !gpc();
  }
  function saveFromPanel() {
    var p = { analytics: false, advertising: false };
    if (bannerEl) {
      bannerEl.querySelectorAll('.consent-cat').forEach(function (input) {
        p[input.getAttribute('data-cat')] = !!input.checked;
      });
    }
    choose(p);
  }
  function choose(p) { writePrefs(p); hideBanner(); applyPrefs(p); }

  /* The banner and the sticky CTA pay bar are BOTH position:fixed at bottom:0.
     The banner has the higher z-index, so it used to sit on top of the buy
     buttons and swallow the click. Publish its height as --consent-h and
     styles.css rides the pay bar above it while the banner is shown. */
  function syncBannerOffset() {
    try {
      if (!bannerEl || bannerEl.hidden) return;
      var h = bannerEl.offsetHeight || 0;
      if (h) document.documentElement.style.setProperty('--consent-h', (h + 10) + 'px');
    } catch (e) {}
  }
  function showBanner(panel) {
    if (isSensitive()) return;
    ensureBanner();
    bannerEl.hidden = false;
    var first = bannerEl.querySelector('.consent-first');
    var pane  = panel ? ensurePanel() : bannerEl.querySelector('.consent-panel');
    if (first) first.hidden = !!panel;
    if (pane)  pane.hidden  = !panel;
    if (panel) setPanelState(readPrefs() || { analytics: false, advertising: false });
    document.documentElement.classList.add('has-consent-banner');
    syncBannerOffset();
    try { window.addEventListener('resize', syncBannerOffset); } catch (e) {}
  }
  function openPanel() { showBanner(true); syncBannerOffset(); }
  function hideBanner() {
    if (bannerEl) bannerEl.hidden = true;
    try {
      document.documentElement.classList.remove('has-consent-banner');
      document.documentElement.style.removeProperty('--consent-h');
      window.removeEventListener('resize', syncBannerOffset);
    } catch (e) {}
  }

  // "Your Privacy Choices" / "Cookie settings" (footer, every page) reopen the
  // category panel showing the current state — withdrawal as easy as consent.
  window.clozoOpenPrivacyChoices = function () { openPanel(); };

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-privacy-choices]') : null;
      if (t) { e.preventDefault(); openPanel(); }
    });

    if (isSensitive()) return;              // never track here, never prompt here

    var p = readPrefs();
    if (p) { applyPrefs(p); return; }       // an explicit choice always wins

    if (gpc()) {                             // honour the signal, record it, no prompt
      writePrefs({ analytics: false, advertising: false });
      return;
    }
    showBanner(false);                       // no decision yet — layer 1
  });
})();
